import React, { useEffect, useRef } from 'react';
import { DESIGN_SYSTEM } from '../constants/designSystem';
import { Globe2, Users, Shield, TrendingUp, ChevronRight } from 'lucide-react';
import RequestForm from './services/RequestForm';
import { useLanguage } from '../contexts/LanguageContext';

function AboutPage() {
  const { t, language } = useLanguage();
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3BA0]/50 via-[#1B3BA0]/30 to-transparent mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="max-w-[780px] relative">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Мы делаем доставку из Китая' : 
                 language === 'en' ? 'We make delivery from China' : 
                 '我们提供从中国的货运服务'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF] animate-gradient-wave">
                {language === 'ru' ? 'простой и надежной' : 
                 language === 'en' ? 'simple and reliable' : 
                 '简单可靠'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
              {language === 'ru' 
                ? 'Более 10 лет опыта в логистике и тысячи довольных клиентов делают нас одним из лидеров рынка'
                : language === 'en' 
                ? 'More than 10 years of experience in logistics and thousands of satisfied customers make us one of the market leaders'
                : '超过10年的物流经验和数千名满意的客户使我们成为市场领导者之一'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { 
                  number: '10+', 
                  label: language === 'ru' ? 'лет опыта' : 
                         language === 'en' ? 'years of experience' : 
                         '年经验' 
                },
                { 
                  number: '5000+', 
                  label: language === 'ru' ? 'клиентов' : 
                         language === 'en' ? 'clients' : 
                         '客户' 
                },
                { 
                  number: '50000+', 
                  label: language === 'ru' ? 'доставок' : 
                         language === 'en' ? 'deliveries' : 
                         '次交付' 
                },
                { 
                  number: '99.9%', 
                  label: language === 'ru' ? 'успешных сделок' : 
                         language === 'en' ? 'successful deals' : 
                         '成功交易' 
                }
              ].map((stat, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1 text-center">
                  <div className="text-4xl font-bold text-[#1B3BA0] mb-4">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Наши преимущества' : 
             language === 'en' ? 'Our Advantages' : 
             '我们的优势'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              {
                icon: <Globe2 className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Глобальное присутствие' : 
                        language === 'en' ? 'Global Presence' : 
                        '全球存在',
                description: language === 'ru' ? 'Офисы в Китае и России обеспечивают полный контроль над логистической цепочкой' : 
                             language === 'en' ? 'Offices in China and Russia provide full control over the logistics chain' : 
                             '中国和俄罗斯的办事处提供对物流链的全面控制'
              },
              {
                icon: <Users className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Персональный подход' : 
                        language === 'en' ? 'Personal Approach' : 
                        '个性化方法',
                description: language === 'ru' ? 'Каждому клиенту предоставляется персональный менеджер, доступный 24/7' : 
                             language === 'en' ? 'Each client is provided with a personal manager available 24/7' : 
                             '每个客户都配有全天候可用的个人经理'
              },
              {
                icon: <Shield className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Гарантия безопасности' : 
                        language === 'en' ? 'Security Guarantee' : 
                        '安全保障',
                description: language === 'ru' ? 'Страхование грузов и полная ответственность за сохранность товаров' : 
                             language === 'en' ? 'Cargo insurance and full responsibility for the safety of goods' : 
                             '货物保险和对货物安全的全部责任'
              },
              {
                icon: <TrendingUp className="w-12 h-12 text-[#1B3BA0]" />,
                title: language === 'ru' ? 'Инновационные решения' : 
                        language === 'en' ? 'Innovative Solutions' : 
                        '创新解决方案',
                description: language === 'ru' ? 'Современные технологии для отслеживания и управления поставками' : 
                             language === 'en' ? 'Modern technologies for tracking and managing supplies' : 
                             '用于跟踪和管理供应的现代技术'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Начните работать с нами сегодня' : 
             language === 'en' ? 'Start working with us today' : 
             '今天就开始与我们合作'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Присоединяйтесь к тысячам довольных клиентов, которые уже оценили преимущества работы с нами'
              : language === 'en' 
              ? 'Join thousands of satisfied customers who have already appreciated the benefits of working with us'
              : '加入数千名已经体验到与我们合作好处的满意客户'}
          </p>
          <RequestForm source={language === 'ru' ? "О нас" : language === 'en' ? "About Us" : "关于我们"} />
        </div>
      </section>

      <style>{`
        @keyframes gradient-wave {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient-wave {
          animation: gradient-wave 6s ease-in-out infinite;
          background-size: 200% auto;
        }

        .animate-on-scroll {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.6s ease-out;
        }

        .animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}

export default AboutPage;